/**
 * Validation Utilities for RSFitness
 * All validation functions for forms
 */

// Check if value is empty
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
};

// Validate email
export const isValidEmail = (email) => {
  if (isEmpty(email)) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Validate phone (10 digits)
export const isValidPhone = (phone) => {
  if (isEmpty(phone)) return false;
  const phoneRegex = /^[0-9]{10}$/;
  return phoneRegex.test(phone.replace(/\D/g, ''));
};

// Validate name (letters and spaces only)
export const isValidName = (name) => {
  if (isEmpty(name)) return false;
  const nameRegex = /^[a-zA-Z\s]+$/;
  return nameRegex.test(name) && name.trim().length >= 2;
};

// Validate pincode (6 digits)
export const isValidPincode = (pincode) => {
  if (isEmpty(pincode)) return true; // Optional
  const pincodeRegex = /^[0-9]{6}$/;
  return pincodeRegex.test(pincode);
};

// Validate password strength
export const isValidPassword = (password) => {
  if (isEmpty(password) || password.length < 6) return false;
  // Check for at least one uppercase, one lowercase, and one number
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  return hasUppercase && hasLowercase && hasNumber;
};

// Calculate age from date of birth
export const calculateAge = (dateOfBirth) => {
  if (isEmpty(dateOfBirth)) return 0;
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

// Validate minimum age
export const isValidAge = (dateOfBirth, minAge = 10) => {
  const age = calculateAge(dateOfBirth);
  return age >= minAge;
};

// Validate image file
export const isValidImage = (file) => {
  if (!file) {
    return { valid: false, message: 'Photo is required' };
  }

  // Check file size (5MB max)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    return { valid: false, message: 'Photo size must be less than 5MB' };
  }

  // Check file type
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, message: 'Only image files are allowed (JPEG, PNG, GIF, WebP)' };
  }

  return { valid: true };
};

// Member registration validation
export const validateMemberForm = (formData, isEditMode = false) => {
  const errors = {};

  // Photo validation (required for new members)
  if (!isEditMode && !formData.photo) {
    errors.photo = 'Photo is required for new members';
  } else if (formData.photo) {
    const photoValidation = isValidImage(formData.photo);
    if (!photoValidation.valid) {
      errors.photo = photoValidation.message;
    }
  }

  // Branch
  if (isEmpty(formData.branch_id)) {
    errors.branch_id = 'Branch is required';
  }

  // First Name
  if (isEmpty(formData.first_name)) {
    errors.first_name = 'First name is required';
  } else if (!isValidName(formData.first_name)) {
    errors.first_name = 'First name must be at least 2 letters and contain only letters and spaces';
  }

  // Last Name
  if (isEmpty(formData.last_name)) {
    errors.last_name = 'Last name is required';
  } else if (!isValidName(formData.last_name)) {
    errors.last_name = 'Last name must be at least 2 letters and contain only letters and spaces';
  }

  // Email (optional but must be valid if provided)
  if (!isEmpty(formData.email)) {
    if (!isValidEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
  }

  // Phone
  if (isEmpty(formData.phone)) {
    errors.phone = 'Phone number is required';
  } else if (!isValidPhone(formData.phone)) {
    errors.phone = 'Phone number must be exactly 10 digits';
  }

  // Address (required)
  if (isEmpty(formData.address)) {
    errors.address = 'Address is required';
  }

  // Date of Birth (optional but must be valid if provided)
  if (!isEmpty(formData.date_of_birth)) {
    if (!isValidAge(formData.date_of_birth, 10)) {
      errors.date_of_birth = 'Member must be at least 10 years old';
    }
  }

  // Pincode (optional but must be valid if provided)
  if (!isEmpty(formData.pincode)) {
    if (!isValidPincode(formData.pincode)) {
      errors.pincode = 'Pincode must be exactly 6 digits';
    }
  }

  // Emergency contact phone (optional but must be valid if provided)
  if (!isEmpty(formData.emergency_contact_phone)) {
    if (!isValidPhone(formData.emergency_contact_phone)) {
      errors.emergency_contact_phone = 'Emergency contact phone must be exactly 10 digits';
    }
  }

  return errors;
};

// Admin registration validation
export const validateAdminForm = (formData) => {
  const errors = {};

  // Photo (required)
  if (!formData.photo) {
    errors.photo = 'Photo is required';
  } else {
    const photoValidation = isValidImage(formData.photo);
    if (!photoValidation.valid) {
      errors.photo = photoValidation.message;
    }
  }

  // Email
  if (isEmpty(formData.email)) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(formData.email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Password
  if (isEmpty(formData.password)) {
    errors.password = 'Password is required';
  } else if (!isValidPassword(formData.password)) {
    errors.password = 'Password must be at least 6 characters with uppercase, lowercase, and number';
  }

  // Confirm Password
  if (isEmpty(formData.confirm_password)) {
    errors.confirm_password = 'Please confirm your password';
  } else if (formData.password !== formData.confirm_password) {
    errors.confirm_password = 'Passwords do not match';
  }

  // First Name
  if (isEmpty(formData.first_name)) {
    errors.first_name = 'First name is required';
  } else if (!isValidName(formData.first_name)) {
    errors.first_name = 'First name must be at least 2 letters and contain only letters and spaces';
  }

  // Last Name
  if (isEmpty(formData.last_name)) {
    errors.last_name = 'Last name is required';
  } else if (!isValidName(formData.last_name)) {
    errors.last_name = 'Last name must be at least 2 letters and contain only letters and spaces';
  }

  // Phone (optional but must be valid if provided)
  if (!isEmpty(formData.phone)) {
    if (!isValidPhone(formData.phone)) {
      errors.phone = 'Phone number must be exactly 10 digits';
    }
  }

  return errors;
};

// Login validation
export const validateLoginForm = (email, password) => {
  const errors = {};

  if (isEmpty(email)) {
    errors.email = 'Email is required';
  } else if (!isValidEmail(email)) {
    errors.email = 'Please enter a valid email address';
  }

  if (isEmpty(password)) {
    errors.password = 'Password is required';
  }

  return errors;
};

// Export all validators
export default {
  isEmpty,
  isValidEmail,
  isValidPhone,
  isValidName,
  isValidPincode,
  isValidPassword,
  calculateAge,
  isValidAge,
  isValidImage,
  validateMemberForm,
  validateAdminForm,
  validateLoginForm
};
